// =============================================================================
// PAYMENT SUCCESS PAGE
// =============================================================================

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/Button';
import { SEOHead } from '@/components/SEO';

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Here you would typically fetch payment details from your backend
    // For now, we'll simulate a successful payment
    if (sessionId) {
      // Simulate API call
      setTimeout(() => {
        setPaymentDetails({
          sessionId,
          status: 'complete',
          amount: '$99.00',
          service: 'Professional Services',
        });
        setIsLoading(false);
      }, 1000);
    } else {
      setIsLoading(false);
    }
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="payment-success">
        <div className="container">
          <div className="loading-state">
            <Clock size={48} className="spinning" />
            <h2>Processing Your Payment</h2>
            <p>Please wait while we confirm your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Payment Successful - Alex Finger Consulting"
        description="Your payment has been processed successfully. Thank you for your business!"
        keywords={['payment success', 'thank you', 'payment confirmed'].join(', ')}
        canonicalUrl="/payment/success"
      />
      <div className="payment-success">
        <div className="container">
          <div className="success-content">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            
            <h1>Payment Successful!</h1>
            <p className="success-message">
              Thank you for your payment. Your transaction has been processed successfully.
            </p>

            {paymentDetails && (
              <div className="payment-details">
                <div className="detail-item">
                  <span className="label">Transaction ID:</span>
                  <span className="value">{paymentDetails.sessionId}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Amount:</span>
                  <span className="value">{paymentDetails.amount}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Service:</span>
                  <span className="value">{paymentDetails.service}</span>
                </div>
              </div>
            )}

            <div className="next-steps">
              <h3>What's Next?</h3>
              <ul>
                <li>
                  <Mail size={16} />
                  <span>You'll receive a receipt via email within the next few minutes</span>
                </li>
                <li>
                  <Clock size={16} />
                  <span>We'll contact you within 24 hours to discuss next steps</span>
                </li>
                <li>
                  <CheckCircle size={16} />
                  <span>Your service will begin as scheduled</span>
                </li>
              </ul>
            </div>

            <div className="action-buttons">
              <Button 
                variant="primary" 
                onClick={() => navigate('/')}
              >
                Return Home
                <ArrowRight size={16} />
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/contact')}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
